import { useState, useRef, useEffect, useCallback } from "react";
import { Bell, CheckCircle, AlertCircle, Info, AlertTriangle, Check, ExternalLink, RefreshCw, Loader2 } from "lucide-react";
import { useListNotifications, useMarkNotificationRead, markAllNotificationsRead, customFetch } from "@workspace/api-client-react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { getListNotificationsQueryKey } from "@workspace/api-client-react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";

const TYPE_ICON: Record<string, React.ReactNode> = {
  success: <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />,
  error: <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />,
  warning: <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />,
  info: <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />,
};

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const [, setLocation] = useLocation();

  const { data: me } = useQuery<Record<string, unknown>>({
    queryKey: ["me"],
    queryFn: () => customFetch<Record<string, unknown>>("/api/me"),
    staleTime: 30_000,
  });

  const hasHrAccess = me?.isSuperAdmin === true || ((me?.rolePermissions as Array<{ module: string }>) || []).some(p => p.module === "hr" || p.module === "all");

  // Destructure isError, isLoading, and refetch so we can distinguish three states:
  //  1. Loading  → show skeleton/spinner
  //  2. Error    → show error fallback with retry (NEVER show "You're all caught up!")
  //  3. Success  → show notifications list or empty state
  const {
    data: notificationsData,
    isError,
    isLoading,
    refetch,
  } = useListNotifications({
    query: { queryKey: getListNotificationsQueryKey(), refetchInterval: 30000, staleTime: 15000 },
  });
  const { mutate: markRead, isPending: isMarkingRead } = useMarkNotificationRead();
  const { toast } = useToast();

  // ─── Mark-all-read mutation ────────────────────────────────────────────────
  // IMPORTANT: Optimistic update, rollback, and cache invalidation are placed
  // in the useMutation hook options (onMutate / onError / onSettled) rather
  // than the mutate() call-site.  TanStack Query silently drops per-call
  // callbacks when the component unmounts before the mutation settles (e.g.
  // the user closes the dropdown panel mid-flight), which was the root cause
  // of the read-state not persisting across refreshes.  Hook-level lifecycle
  // methods survive component unmounting and are always guaranteed to run.
  const { mutate: markAllRead, isPending: isMarkingAllRead } = useMutation({
    mutationKey: ["markAllNotificationsRead"],
    mutationFn: () => markAllNotificationsRead(),
    onMutate: async () => {
      // Cancel any in-flight refetches so they don't overwrite our optimistic update
      await qc.cancelQueries({ queryKey: getListNotificationsQueryKey() });
      // Snapshot previous state for rollback
      const previousData = qc.getQueryData(getListNotificationsQueryKey());
      // Apply optimistic update: mark all as read in the local cache immediately
      qc.setQueryData(getListNotificationsQueryKey(), (old: any) => {
        if (!Array.isArray(old)) return old;
        return old.map((item: any) => ({ ...item, isRead: true }));
      });
      return { previousData };
    },
    onError: (err: any, _variables, context) => {
      // Roll back to the snapshot on failure
      if (context?.previousData !== undefined) {
        qc.setQueryData(getListNotificationsQueryKey(), context.previousData);
      }
      toast({
        title: "Failed to mark all notifications as read",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      // Always re-sync from server after success or failure to ensure consistency
      qc.invalidateQueries({ queryKey: getListNotificationsQueryKey() });
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["notifications-unread-count"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "stats"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "employee-stats"] });
      refetch();
    },
  });

  // Only derive notifications from successfully fetched data.
  // When isError is true, notificationsData is undefined — so we must NOT default
  // to [] here, otherwise the empty-state branch is triggered incorrectly.
  const rawNotifications = isError ? [] : (Array.isArray(notificationsData) ? notificationsData : []);
  const seenKeys = new Set<string>();
  const notifications = rawNotifications.filter((n) => {
    if (!n || !n.id) return false;
    if (n.isRead) return false;
    const timeBucket = Math.floor(new Date(n.createdAt).getTime() / 120000);
    const normTitle = (n.title || "").toLowerCase().replace(/^(leave\s+application\s+|leave\s+)/i, "").replace(/[^a-z0-9]/g, "");
    const key = `${n.id}:${normTitle}:${n.link ?? ""}:${timeBucket}`;
    if (seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });
  const unread = isError || isLoading ? 0 : notifications.length;

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const invalidateNotificationQueries = useCallback(() => {
    qc.invalidateQueries({ queryKey: getListNotificationsQueryKey() });
    qc.invalidateQueries({ queryKey: ["notifications"] });
    qc.invalidateQueries({ queryKey: ["notifications-unread-count"] });
    qc.invalidateQueries({ queryKey: ["dashboard", "stats"] });
    qc.invalidateQueries({ queryKey: ["dashboard", "employee-stats"] });
  }, [qc]);

  function handleMarkRead(id: number) {
    const previous = qc.getQueryData(getListNotificationsQueryKey());
    qc.setQueryData(getListNotificationsQueryKey(), (old: any) => {
      if (!Array.isArray(old)) return old;
      return old.map((item: any) => (item.id === id ? { ...item, isRead: true } : item));
    });
    markRead({ id }, {
      onSuccess: () => {
        invalidateNotificationQueries();
        refetch();
      },
      onError: (err: any) => {
        if (previous !== undefined) {
          qc.setQueryData(getListNotificationsQueryKey(), previous);
        }
        toast({
          title: "Failed to mark notification as read",
          description: err?.message || "Please try again.",
          variant: "destructive",
        });
      },
    });
  }

  function handleMarkAllRead(e?: React.MouseEvent) {
    e?.stopPropagation();
    if (isMarkingAllRead) return;
    // The optimistic update, rollback, and invalidation are all handled by the
    // useMutation hook options above — no extra logic needed here.
    markAllRead();
  }

  // Safety map: legacy / incorrect links -> valid app routes.
  // Prevents 404s when stored notification links don't match the current route tree.
  const KNOWN_ROUTES = new Set([
    "/dashboard", "/organization", "/users", "/audit-log",
    "/hr", "/employee", "/my-portal", "/account-settings", "/settings", "/finance", "/catalog", "/payroll",
    "/tally", "/inventory", "/procurement", "/crm", "/operations",
    "/admin", "/reports", "/recruitment", "/quality", "/manufacturing",
    "/approvals", "/performance", "/service", "/integrations",
    "/dms", "/ld", "/travel", "/helpdesk", "/dev-tools",
  ]);

  const LINK_FALLBACK: Record<string, string> = {
    "/leave":              "/employee",
    "/hr/my-leave":        "/employee",
    "/expenses":           "/travel",
    "/projects":           "/operations",
    "/payroll/me":         "/payroll?tab=my-payslips",
    "/payroll/me/payslips": "/payroll?tab=my-payslips",
    "/payroll/my-payslips": "/payroll?tab=my-payslips",
    "/payroll/salary-slips": "/payroll?tab=my-payslips",
    "/operations/finance/payroll/salary-slips": "/payroll?tab=my-payslips",
    "/finance/payroll/salary-slips": "/payroll?tab=my-payslips",
    "/operations/finance/payroll/runs": "/payroll?tab=runs",
    "/finance/payroll/runs": "/payroll?tab=runs",
    "/payroll/runs": "/payroll?tab=runs",
    "/operations/finance/payroll": "/payroll?tab=runs",
    "/finance/payroll": "/payroll?tab=runs",
    "/operations/tickets": "/operations?tab=tickets",
    "/finance/purchase":   "/finance?tab=purchase",
    "/finance/vouchers":   "/finance?tab=vouchers",
    "/finance/ar":         "/finance?tab=sales",
    "/finance/vendors":    "/finance?tab=purchase",
    "/finance/sales":      "/finance?tab=sales",
    "/finance/banking":    "/finance?tab=banking",
    "/finance/gst":        "/finance?tab=gst",
    "/finance/tds":        "/finance?tab=tds",
    "/finance/einvoice":   "/finance?tab=einvoice",
    "/finance/statements": "/finance?tab=statements",
    "/finance/reports":    "/finance?tab=reports",
    "/finance/budget":     "/finance?tab=budget",
    "/finance/cost-centres": "/finance?tab=cost-centres",
    "/finance/capex":      "/finance?tab=capex",
    "/finance/helpdesk":   "/finance?tab=helpdesk",
    "/finance/payment-codes": "/finance?tab=payment-codes",
    "/payroll/compliance":   "/payroll?tab=compliance",
    "/payroll/statutory":    "/payroll?tab=statutory",
    "/hr/expense-claims":    "/hr?tab=expense-claims",
    "/hr/onboarding":        "/hr?tab=onboarding",
    "/hr/attendance":        "/hr?tab=attendance",
    "/hr/me/attendance":     "/employee?tab=attendance",
    "/hr/leave":             "/hr?tab=leave-approvals",
    "/crm/opportunities":    "/crm?tab=opportunities",
  };

  function resolveLink(raw: string): string {
    // 0. Role-safe Expense Claims routing
    const isExpenseClaimLink =
      raw === "/hr/expense-claims" ||
      raw === "/hr?tab=expense-claims" ||
      raw.startsWith("/hr/expense-claims?") ||
      raw.startsWith("/hr/expense-claims/") ||
      raw.startsWith("/hr-panel/expense-claims") ||
      raw.startsWith("/admin/expense-claims") ||
      raw.startsWith("/my-portal/expense-claims") ||
      raw.startsWith("/employee/expense-claims") ||
      raw.startsWith("/employee/expenses") ||
      raw.startsWith("/operations/employee/expenses") ||
      raw.startsWith("/operations/expenses");

    if (isExpenseClaimLink) {
      if (!hasHrAccess || raw.startsWith("/my-portal/") || raw.startsWith("/employee/") || raw.startsWith("/operations/employee/") || raw.startsWith("/operations/expenses")) {
        const idMatch = raw.match(/\/(\d+)(?:\?.*)?$/) || raw.match(/[?&]id=(\d+)/) || raw.match(/[?&]claimId=(\d+)/);
        return idMatch ? `/employee?tab=expenses&claimId=${idMatch[1]}` : "/employee?tab=expenses";
      }
      return "/hr?tab=expense-claims";
    }

    if (LINK_FALLBACK[raw]) return LINK_FALLBACK[raw];
    const deptHelpdeskMatch = raw.match(/^(?:\/operations|\/finance|\/hr|\/helpdesk)?\?tab=helpdesk&ticketId=(\d+)/);
    if (deptHelpdeskMatch) return `/helpdesk?ticketId=${deptHelpdeskMatch[1]}`;
    const ticketMatch = raw.match(/^\/helpdesk\/tickets\/(\d+)/);
    if (ticketMatch) return `/helpdesk?ticketId=${ticketMatch[1]}`;

    // 1. Salary slips deep links (individual employee self-service view)
    const opFinPayrollSlipIdMatch = raw.match(/^\/operations\/finance\/payroll\/salary-slips\/(\d+)/);
    if (opFinPayrollSlipIdMatch) {
      return `/payroll?tab=my-payslips&entryId=${opFinPayrollSlipIdMatch[1]}`;
    }
    const finPayrollSlipIdMatch = raw.match(/^\/finance\/payroll\/salary-slips\/(\d+)/);
    if (finPayrollSlipIdMatch) {
      return `/payroll?tab=my-payslips&entryId=${finPayrollSlipIdMatch[1]}`;
    }
    const payrollSlipIdMatch = raw.match(/^\/payroll\/salary-slips\/(\d+)/);
    if (payrollSlipIdMatch) {
      return `/payroll?tab=my-payslips&entryId=${payrollSlipIdMatch[1]}`;
    }

    const opFinPayrollSlipsMatch = raw.match(/^\/operations\/finance\/payroll\/salary-slips(?:\?.*)?$/);
    if (opFinPayrollSlipsMatch) {
      const search = raw.includes("?") ? raw.split("?")[1] : "";
      return `/payroll?tab=my-payslips${search ? `&${search}` : ""}`;
    }
    const finPayrollSlipsMatch = raw.match(/^\/finance\/payroll\/salary-slips(?:\?.*)?$/);
    if (finPayrollSlipsMatch) {
      const search = raw.includes("?") ? raw.split("?")[1] : "";
      return `/payroll?tab=my-payslips${search ? `&${search}` : ""}`;
    }
    const payrollSlipsMatch = raw.match(/^\/payroll\/salary-slips(?:\?.*)?$/);
    if (payrollSlipsMatch) {
      const search = raw.includes("?") ? raw.split("?")[1] : "";
      return `/payroll?tab=my-payslips${search ? `&${search}` : ""}`;
    }
    const myPayslipsMatch = raw.match(/^\/payroll\/(?:my-payslips|me\/payslips)(?:\?.*)?$/);
    if (myPayslipsMatch) {
      const search = raw.includes("?") ? raw.split("?")[1] : "";
      return `/payroll?tab=my-payslips${search ? `&${search}` : ""}`;
    }

    // 2. Payroll runs deep links (Admin / Finance / HR management view)
    const opFinPayrollRunMatch = raw.match(/^\/operations\/finance\/payroll\/runs\/(\d+)/);
    if (opFinPayrollRunMatch) {
      return `/payroll?tab=runs&runId=${opFinPayrollRunMatch[1]}`;
    }
    const finPayrollRunMatch = raw.match(/^\/finance\/payroll\/runs\/(\d+)/);
    if (finPayrollRunMatch) {
      return `/payroll?tab=runs&runId=${finPayrollRunMatch[1]}`;
    }
    const payrollRunMatch = raw.match(/^\/payroll\/runs\/(\d+)/);
    if (payrollRunMatch) {
      return `/payroll?tab=runs&runId=${payrollRunMatch[1]}`;
    }

    const opFinPayrollMatch = raw.match(/^\/operations\/finance\/payroll(?:\/runs)?(?:\/(\d+))?(?:\?.*)?$/);
    if (opFinPayrollMatch) {
      const runId = opFinPayrollMatch[1];
      const search = raw.includes("?") ? raw.split("?")[1] : "";
      if (runId) return `/payroll?tab=runs&runId=${runId}`;
      return `/payroll?tab=runs${search ? `&${search}` : ""}`;
    }
    const finPayrollMatch = raw.match(/^\/finance\/payroll(?:\/runs)?(?:\/(\d+))?(?:\?.*)?$/);
    if (finPayrollMatch) {
      const runId = finPayrollMatch[1];
      const search = raw.includes("?") ? raw.split("?")[1] : "";
      if (runId) return `/payroll?tab=runs&runId=${runId}`;
      return `/payroll?tab=runs${search ? `&${search}` : ""}`;
    }
    if (raw === "/payroll/runs" || raw.startsWith("/payroll/runs?")) return raw.replace(/^\/payroll\/runs/, "/payroll?tab=runs");
    if (raw === "/finance/payroll" || raw.startsWith("/finance/payroll?")) return raw.replace(/^\/finance\/payroll/, "/payroll?tab=runs");
    if (raw.startsWith("/operations?tab=tickets")) return raw;
    const hrEmployeeMatch = raw.match(/^\/hr\/employees\/(\d+)/);
    if (hrEmployeeMatch) return `/hr/employee/${hrEmployeeMatch[1]}`;
    const pathPart = raw.split("?")[0].split("#")[0];
    const top = "/" + pathPart.split("/")[1];
    if (KNOWN_ROUTES.has(top)) return raw;
    return raw;
  }

  // Navigate FIRST, then mark as read. This prevents the global query
  // invalidation (triggered by the markRead mutation's onSuccess in the
  // MutationCache) from interfering with the navigation.
  // IMPORTANT: wouter v3's setLocation does NOT reliably handle query strings --
  // passing "/helpdesk?ticketId=42" causes the route matcher to see the full
  // string as the pathname, resulting in a 404. We strip the query string
  // before navigating, then restore it via replaceState so that useSearch()
  // on the target page can read the params.
  const handleNotificationClick = useCallback((n: { id: number; isRead: boolean; link?: string | null }) => {
    if (n.link) {
      setOpen(false);
      const resolved = resolveLink(n.link);
      const [pathname, search] = resolved.split("?");
      setLocation(pathname);
      if (search) {
        window.history.replaceState(null, "", `${pathname}?${search}`);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
    }
    // Fire-and-forget mark read AFTER navigation so the mutation side-effects
    // (global invalidateQueries) don't cause the target page to blink or
    // redirect back to the dashboard.
    if (!n.isRead) handleMarkRead(n.id);
  }, [setLocation]);

  // Render the dropdown body based on fetch state.
  // Three explicit branches ensure error is never silently shown as empty state.
  function renderDropdownBody() {
    // State 1: Loading skeleton
    if (isLoading) {
      return (
        <div className="py-8 flex flex-col items-center gap-3 text-muted-foreground">
          <RefreshCw className="h-6 w-6 opacity-40 animate-spin" />
          <p className="text-sm">Loading notifications...</p>
        </div>
      );
    }

    // State 2: API error (503, 500, network failure, etc.)
    // Must NOT fall through to the empty state in this case.
    if (isError) {
      return (
        <div className="py-10 flex flex-col items-center gap-3 text-muted-foreground px-4">
          <AlertCircle className="h-8 w-8 text-red-400 opacity-70" />
          <p className="text-sm text-center font-medium text-foreground/80">Unable to load notifications</p>
          <p className="text-xs text-center text-muted-foreground">
            The notification service is temporarily unavailable. Please try again.
          </p>
          <button
            onClick={() => refetch()}
            className="mt-1 flex items-center gap-1.5 rounded-md bg-muted px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/80 transition-colors"
          >
            <RefreshCw className="h-3 w-3" />
            Retry
          </button>
        </div>
      );
    }

    // State 3a: Successful fetch, no unread notifications
    if (notifications.length === 0) {
      return (
        <div className="py-12 flex flex-col items-center gap-2 text-muted-foreground">
          <Bell className="h-8 w-8 opacity-30" />
          <p className="text-sm">You're all caught up!</p>
        </div>
      );
    }

    // State 3b: Successful fetch with notifications
    return notifications.slice(0, 30).map((n) => (
      <div
        key={n.id}
        onClick={() => handleNotificationClick(n)}
        className={`flex gap-3 px-4 py-3 transition-colors ${n.link ? "cursor-pointer hover:bg-muted/50" : !n.isRead ? "cursor-pointer hover:bg-muted/50" : ""} ${!n.isRead ? "bg-blue-50/60 dark:bg-blue-500/10" : ""}`}
      >
        <div className="mt-0.5 flex-shrink-0">
          {TYPE_ICON[n.type] ?? TYPE_ICON.info}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className={`text-sm leading-snug ${!n.isRead ? "font-semibold text-foreground" : "font-medium text-foreground/80"}`}>
              {n.title}
            </p>
            {n.link && (
              <ExternalLink className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground mt-0.5" />
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{n.message}</p>
          <p className="text-[11px] text-muted-foreground mt-1">{formatRelativeTime(n.createdAt)}</p>
        </div>
        {!n.isRead && (
          <div className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-blue-500" />
        )}
      </div>
    ));
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white leading-none">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed left-2 right-2 top-14 w-auto sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 rounded-xl border border-border bg-card shadow-2xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/50">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-semibold text-foreground">Notifications</span>
              {unread > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                  {unread}
                </span>
              )}
            </div>
            {unread > 0 && (
              <button
                onClick={handleMarkAllRead}
                disabled={isMarkingAllRead}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isMarkingAllRead ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Check className="h-3 w-3" />
                )}
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto divide-y divide-border/40">
            {renderDropdownBody()}
          </div>

          {!isError && !isLoading && notifications.length > 0 && (
            <div className="border-t border-border px-4 py-2.5 text-center">
              <span className="text-xs text-muted-foreground">{notifications.length} notification{notifications.length !== 1 ? "s" : ""} total</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
